package at.theatervogelweide.admin.contract.sponsors

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.validate
import kotlinx.serialization.Serializable

/** A sponsor in the public site's order (#94). */
@Serializable
data class SponsorEntry(
    val id: Long,
    val version: Long,
    val name: String,
    val websiteUrl: String?,
    val logoUuid: String?,
    val visible: Boolean,
) {
    val versionedId: VersionedId get() = VersionedId(id, version)

    val draft: SponsorDraft get() = SponsorDraft(name, websiteUrl.orEmpty(), logoUuid, visible)

    /** The website's host without `www.`, as the list shows it; `null` without a website. */
    val websiteHost: String? get() = websiteUrl?.let(::hostOf)
}

/** What the editor creates and saves. A blank website is stored as none; without a logo the public site shows the name. */
@Serializable
data class SponsorDraft(
    val name: String = "",
    val websiteUrl: String = "",
    /** An upload of the sponsor category, SVG allowed (#93). */
    val logoUuid: String? = null,
    val visible: Boolean = true,
) {
    fun validate(): List<FieldError> = validate {
        field(NAME, name) { required(); maxLength(255) }
        field(WEBSITE, websiteUrl.trim()) { maxLength(2000) }
        check(WEBSITE, websiteUrl.isBlank() || WEB_ADDRESS.matches(websiteUrl.trim())) {
            "Bitte eine Adresse beginnend mit https:// eingeben."
        }
    }

    companion object {
        const val NAME = "name"
        const val WEBSITE = "websiteUrl"
        const val LOGO = "logo"

        /** An absolute http(s) address with a host that has a dot, like the reservation link of an act. */
        private val WEB_ADDRESS = Regex("^https?://[^\\s/?#]+\\.[^\\s]*$", RegexOption.IGNORE_CASE)
    }
}

private fun hostOf(url: String): String =
    url.substringAfter("://").substringBefore('/').substringBefore('?').substringBefore('#')
        .substringAfterLast('@').substringBefore(':').lowercase().removePrefix("www.")
